import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { web3FromAddress } from "@polkadot/extension-dapp";
import { cryptoWaitReady } from "@polkadot/util-crypto";
import styled from "styled-components";
import WalletConnect from "../../components/WalletConnect";
import { Flex, FlexBetween } from "../../components/styled/flex";
import Layout from "../../components/layout";
import { Panel } from "../../components/styled/panel";
import { Inter_14_500, Inter_12_400, Inter_12_600 } from "../../styles/text";
import { Button } from "../../components/styled/buttons";
import Input from "../../components/input";
import { ColoredInterLink } from "../../components/styled/link";
import copyToClipboard from "copy-to-clipboard";
import { addToast } from "../../store/reducers/toastSlice";
import Pagination from "../../components/pagination";
import { useSearchParams } from "react-router-dom";
import { useChainApi } from "../../utils/hooks/chain/useChainApi";
import NoData from "../../components/noData";

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  padding: 8px 0;
`;

const PageTitle = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: ${(p) => p.theme.fontPrimary};
  margin: 0 0 32px 0;
  line-height: 1.2;
`;

const WalletSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: ${(p) => p.theme.fillPanel};
  border-radius: 8px;
  border: 1px solid ${(p) => p.theme.strokeBase};
`;

const WalletInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
`;

const WalletActions = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const SmallButton = styled(Button)`
  padding: 6px 12px;
  font-size: 12px;
  height: auto;
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(9, 29, 57, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: ${(p) => p.theme.fillPanel};
  border-radius: 12px;
  padding: 24px;
  max-width: 800px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
`;

const DocumentViewer = styled.pre`
  background: ${(p) => p.theme.fillAlpha};
  border: 1px solid ${(p) => p.theme.strokeBase};
  border-radius: 8px;
  padding: 16px;
  margin: 16px 0;
  overflow-x: auto;
  font-family: monospace;
  font-size: 14px;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
`;

const ViewButton = styled.button`
  background: transparent;
  border: 1px solid ${(p) => p.theme.theme500};
  color: ${(p) => p.theme.theme500};
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${(p) => p.theme.theme500};
    color: white;
  }
`;

const WalletAddress = styled.div`
  ${Inter_14_500};
  color: ${(p) => p.theme.fontPrimary};
`;

const WalletTitle = styled.h4`
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: ${(p) => p.theme.fontPrimary};
`;

const WalletDetails = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 2px;
`;

const WalletName = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: ${(p) => p.theme.fontPrimary};
`;

const WalletAddressShort = styled.span`
  font-family: monospace;
  font-size: 13px;
  color: ${(p) => p.theme.fontSecondary};
  background: ${(p) => p.theme.fillAlpha};
  padding: 4px 8px;
  border-radius: 6px;
`;

const BalanceAmount = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: ${(p) => p.theme.fontPrimary};
  margin-right: 4px;
`;

const BalanceCurrency = styled.span`
  font-size: 12px;
  font-weight: 400;
  color: ${(p) => p.theme.fontSecondary};
`;

const StatusIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const StatusDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${(p) => (p.connected ? "#10b981" : "#ef4444")};
  animation: ${(p) => (p.connected ? "pulse 2s ease-in-out infinite" : "none")};

  @keyframes pulse {
    0% {
      opacity: 1;
      transform: scale(1);
    }
    50% {
      opacity: 0.8;
      transform: scale(1.1);
    }
    100% {
      opacity: 1;
      transform: scale(1);
    }
  }
`;

const StatusText = styled.span`
  font-size: 12px;
  color: ${(p) => p.theme.fontSecondary};
`;

const RegistrationPanel = styled(Panel)`
  padding: 32px;

  h3 {
    margin: 0 0 24px 0;
    font-size: 18px;
    font-weight: 600;
    color: ${(p) => p.theme.fontPrimary};
  }
`;

const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 16px;
`;

const Label = styled.label`
  ${Inter_14_500};
  color: ${(p) => p.theme.fontPrimary};
  margin-bottom: 8px;
`;

const InputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const TextArea = styled.textarea`
  padding: 12px;
  border: 1px solid ${(p) => p.theme.strokeBase};
  border-radius: 8px;
  background: ${(p) => p.theme.fillPanel};
  color: ${(p) => p.theme.fontPrimary};
  font-family: inherit;
  font-size: 14px;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: ${(p) => p.theme.theme500};
  }

  &::placeholder {
    color: ${(p) => p.theme.fontTertiary};
  }
`;

const Select = styled.select`
  padding: 12px;
  border: 1px solid ${(p) => p.theme.strokeBase};
  border-radius: 8px;
  background: ${(p) => p.theme.fillPanel};
  color: ${(p) => p.theme.fontPrimary};
  font-family: inherit;
  font-size: 14px;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: ${(p) => p.theme.theme500};
  }
`;

const ErrorMessage = styled.div`
  ${Inter_12_400};
  color: ${(p) => p.theme.error500};
  margin-top: 4px;
`;

const SuccessMessage = styled.div`
  ${Inter_12_400};
  color: ${(p) => p.theme.success500};
  margin-top: 4px;
`;

const Balance = styled.div`
  ${Inter_12_400};
  color: ${(p) => p.theme.fontSecondary};
`;

const CopyButton = styled.button`
  all: unset;
  margin-left: 8px;
  padding: 2px 6px;
  color: ${({ theme }) => theme.fontSecondary};
  background: ${({ theme }) => theme.fillSub};
  border-radius: 4px;
  ${Inter_12_600};
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    color: ${({ theme }) => theme.fontPrimary};
    background: ${({ theme }) => theme.fillAlpha};
  }
`;

const DidsPanel = styled(Panel)`
  padding: 32px;

  h3 {
    margin: 0 0 24px 0;
    font-size: 18px;
    font-weight: 600;
    color: ${(p) => p.theme.fontPrimary};
  }
`;

const Tag = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  ${Inter_12_400};
  background: ${(p) => p.theme.fillAlpha};
  color: ${(p) => p.theme.fontSecondary};
`;

const DidsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 16px;

  thead {
    tr {
      border-bottom: 1px solid ${(p) => p.theme.strokeBase};
    }

    th {
      padding: 12px;
      text-align: left;
      font-weight: 500;
      color: ${(p) => p.theme.fontSecondary};

      &:nth-child(1) {
        width: 15%;
      } /* Account */
      &:nth-child(2) {
        width: 35%;
      } /* DID Identifier */
      &:nth-child(3) {
        width: 12%;
        text-align: center;
      } /* Name */
      &:nth-child(4) {
        width: 10%;
        text-align: center;
      } /* Role */
      &:nth-child(5) {
        width: 14%;
        text-align: center;
      } /* Created At */
      &:nth-child(6) {
        width: 14%;
        text-align: center;
      } /* Updated At */
      &:nth-child(7) {
        width: 10%;
        text-align: center;
      } /* Actions */
    }
  }

  tbody {
    tr {
      border-bottom: 1px solid ${(p) => p.theme.strokeBase};

      &:hover {
        background: ${(p) => p.theme.fillAlpha};
      }
    }

    td {
      padding: 12px;
      color: ${(p) => p.theme.fontPrimary};
      vertical-align: top;

      &:nth-child(2) {
        word-break: break-all;
        font-family: monospace;
        font-size: 12px;
      }

      &:nth-child(3),
      &:nth-child(4) {
        text-align: center;
      }

      &:nth-child(5),
      &:nth-child(6) {
        text-align: center;
        font-family: monospace;
        font-size: 12px;
      }
    }
  }
`;

function DidPage() {
  const api = useChainApi();
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const page = parseInt(searchParams.get("page") || "1");

  // Helper functions
  const hexToString = (hex) => {
    if (!hex) return "";
    const cleanHex = hex.startsWith("0x") ? hex.slice(2) : hex;
    const bytes = [];
    for (let i = 0; i < cleanHex.length; i += 2) {
      bytes.push(parseInt(cleanHex.substr(i, 2), 16));
    }
    return new TextDecoder().decode(new Uint8Array(bytes));
  };

  const formatBalance = (balance) => {
    if (!balance) return "0";
    const decimals = 10; // Assuming 10 decimals
    const divisor = 10 ** decimals;
    const beforeDecimal = Math.floor(balance / divisor).toString();
    const afterDecimal = (balance % divisor).toString().padStart(decimals, "0");
    const trimmedAfterDecimal = afterDecimal.slice(0, 4);
    return `${beforeDecimal}.${trimmedAfterDecimal}`;
  };

  const copyAddress = (address) => {
    copyToClipboard(address);
    dispatch(addToast({ message: "Address copied!", type: "info" }));
  };

  // Helper function to format timestamps
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "-";
    // If it's a block number, show it as is for now
    if (typeof timestamp === "number" && timestamp < 1000000) {
      return `Block ${timestamp}`;
    }
    // If it's a Unix timestamp, format as date
    try {
      const date = new Date(timestamp * 1000);
      return date.toLocaleDateString();
    } catch {
      return timestamp.toString();
    }
  };

  const [selectedAccount, setSelectedAccount] = useState(null);
  const [showWalletModal, setShowWalletModal] = useState(false);
  const [balance, setBalance] = useState("");
  const [existingDid, setExistingDid] = useState(null);

  const [didIdentifier, setDidIdentifier] = useState("");
  const [didName, setDidName] = useState("");
  const [didDocument, setDidDocument] = useState("");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [allDids, setAllDids] = useState([]);
  const [totalDids, setTotalDids] = useState(0);
  const [loadingDids, setLoadingDids] = useState(true);
  const [selectedDidDoc, setSelectedDidDoc] = useState(null);
  const [showDocModal, setShowDocModal] = useState(false);

  const itemsPerPage = 20;

  // Initialize crypto
  useEffect(() => {
    cryptoWaitReady();
  }, []);

  // Fetch account data when account is selected
  useEffect(() => {
    let isMounted = true;

    const fetchAccountData = async () => {
      if (!api || !selectedAccount) {
        if (isMounted) {
          setBalance("");
          setExistingDid(null);
        }
        return;
      }

      try {
        // Get balance
        const {
          data: { free },
        } = await api.query.system.account(selectedAccount.address);

        if (isMounted) {
          setBalance(formatBalance(free.toString()));
        }

        // Check if DID already exists
        const did = await api.query.did.dids(selectedAccount.address);
        if (isMounted) {
          if (!did.isEmpty) {
            const didData = did.toJSON();
            setExistingDid({
              ...didData,
              identifier: hexToString(didData.identifier),
              name: didData.name ? hexToString(didData.name) : null,
              document: hexToString(didData.document),
            });
          } else {
            setExistingDid(null);
          }
        }
      } catch (error) {
        if (isMounted) {
          console.error("Error fetching account data:", error);
        }
      }
    };

    fetchAccountData();

    return () => {
      isMounted = false;
    };
  }, [api, selectedAccount]);

  // Fetch all DIDs
  useEffect(() => {
    let isMounted = true;

    const fetchDids = async () => {
      if (!api) return;

      if (isMounted) {
        setLoadingDids(true);
      }

      try {
        const entries = await api.query.did.dids.entries();
        const didsData = entries.map(([key, value]) => {
          const account = key.args[0].toString();
          const didInfo = value.toJSON();

          return {
            account,
            ...didInfo,
            identifier: hexToString(didInfo.identifier),
            name: didInfo.name ? hexToString(didInfo.name) : null,
            document: hexToString(didInfo.document),
            role: didInfo.role === 0 ? "User" : "Validator",
          };
        });

        if (isMounted) {
          setAllDids(didsData);
          setTotalDids(didsData.length);
        }
      } catch (error) {
        if (isMounted) {
          console.error("Failed to fetch DIDs:", error);
        }
      } finally {
        if (isMounted) {
          setLoadingDids(false);
        }
      }
    };

    fetchDids();

    return () => {
      isMounted = false;
    };
  }, [api]);

  const generateDidIdentifier = () => {
    if (!selectedAccount?.address) {
      setError("Please select an account first");
      return;
    }
    const didId = `did:dropp:${selectedAccount.address}`;
    setDidIdentifier(didId);

    // Auto-generate DID document
    const defaultDocument = JSON.stringify(
      {
        "@context": "https://www.w3.org/ns/did/v1",
        id: didId,
        verificationMethod: [],
        service: [],
      },
      null,
      2,
    );
    setDidDocument(defaultDocument);

    setError(""); // Clear any previous errors
  };

  const viewDidDocument = (did) => {
    try {
      // Try to parse and format the document as JSON
      const parsed = JSON.parse(did.document);
      setSelectedDidDoc({
        ...did,
        formattedDoc: JSON.stringify(parsed, null, 2),
      });
    } catch {
      // If not valid JSON, show as-is
      setSelectedDidDoc({
        ...did,
        formattedDoc: did.document,
      });
    }
    setShowDocModal(true);
  };

  const handleRegisterDid = async () => {
    console.log("🔥 Register DID button clicked!");
    console.log("API:", api);
    console.log("Selected Account:", selectedAccount);
    console.log("DID Identifier:", didIdentifier);
    console.log("DID Document:", didDocument);

    if (!api || !selectedAccount) {
      console.log("❌ Missing API or account");
      setError("Please connect wallet first");
      return;
    }

    if (!didIdentifier) {
      console.log("❌ Missing DID identifier");
      setError("DID identifier is required");
      return;
    }

    if (!didDocument) {
      console.log("❌ Missing DID document");
      setError("DID document is required");
      return;
    }

    console.log("✅ All validations passed, starting registration...");
    setLoading(true);
    setError("");
    setSuccess("");

    try {
      console.log("🔌 Getting injector...");
      const injector = await web3FromAddress(selectedAccount.address);
      console.log("💉 Got injector:", injector);

      // Create the transaction
      console.log("📝 Creating transaction...");
      console.log("DID Identifier being sent:", didIdentifier);
      console.log("DID Name being sent:", didName);
      console.log("DID Document being sent:", didDocument);

      const tx = api.tx.did.registerDid(
        didIdentifier,
        didName ? didName : null,
        didDocument,
      );
      console.log("📋 Transaction created:", tx);
      console.log("Transaction data:", tx.toHex());

      // Sign and send
      console.log("✍️ Signing and sending transaction...");
      await new Promise((resolve, reject) => {
        tx.signAndSend(
          selectedAccount.address,
          { signer: injector.signer },
          ({ status, events, dispatchError }) => {
            console.log("📡 Transaction status:", status.type);

            if (status.isInBlock) {
              console.log(
                `✅ Transaction included in block ${status.asInBlock}`,
              );

              // Check for errors
              if (dispatchError) {
                console.log("❌ Dispatch error:", dispatchError);
                if (dispatchError.isModule) {
                  const decoded = api.registry.findMetaError(
                    dispatchError.asModule,
                  );
                  console.log("❌ Decoded error:", decoded);
                  reject(
                    new Error(
                      `${decoded.section}.${decoded.name}: ${decoded.docs.join(
                        " ",
                      )}`,
                    ),
                  );
                } else {
                  reject(new Error(dispatchError.toString()));
                }
                return;
              }

              // Check events
              events.forEach(({ event }) => {
                console.log("📅 Event:", event.section, event.method);
                if (api.events.did.DidRegistered.is(event)) {
                  console.log("🎉 DID successfully registered!");
                  setSuccess("DID successfully registered!");
                  dispatch(
                    addToast({
                      message: "DID successfully registered!",
                      type: "success",
                    }),
                  );
                  // Refresh the DID list
                  window.location.reload();
                }
              });

              resolve();
            }
          },
        ).catch((error) => {
          console.log("❌ Transaction error:", error);
          reject(error);
        });
      });
    } catch (err) {
      console.log("💥 Registration failed:", err);
      setError("Failed to register DID: " + err.message);
      dispatch(
        addToast({
          message: "Failed to register DID: " + err.message,
          type: "error",
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  const paginatedDids = allDids.slice(
    (page - 1) * itemsPerPage,
    page * itemsPerPage,
  );

  return (
    <Layout>
      <Wrapper>
        <PageTitle>Decentralized Identifiers (DIDs)</PageTitle>

        {/* Wallet Section */}
        <WalletSection>
          <WalletInfo>
            <WalletTitle>Connected Wallet</WalletTitle>
            {selectedAccount ? (
              <>
                <WalletDetails>
                  <StatusIndicator>
                    <StatusDot connected={true} />
                    <StatusText>Connected</StatusText>
                  </StatusIndicator>
                  <WalletName>
                    {selectedAccount.meta?.name || "Unknown Account"}
                  </WalletName>
                  <WalletAddressShort>
                    {selectedAccount.address.slice(0, 6)}...
                    {selectedAccount.address.slice(-6)}
                  </WalletAddressShort>
                </WalletDetails>
                <div
                  style={{ fontSize: "12px", color: "var(--fontSecondary)" }}
                >
                  {/* Balance display removed */}
                  {existingDid && (
                    <span
                      style={{
                        color: "#10b981",
                        fontWeight: "500",
                      }}
                    >
                      • DID Registered
                    </span>
                  )}
                </div>
              </>
            ) : (
              <div style={{ marginTop: "8px" }}>
                <StatusIndicator>
                  <StatusDot connected={false} />
                  <StatusText>Not connected</StatusText>
                </StatusIndicator>
                <p
                  style={{
                    marginTop: "8px",
                    fontSize: "14px",
                    color: "var(--fontSecondary)",
                  }}
                >
                  Connect a wallet to register and manage DIDs
                </p>
              </div>
            )}
          </WalletInfo>
          <WalletActions>
            {selectedAccount && (
              <CopyButton
                onClick={() => copyAddress(selectedAccount.address)}
                style={{
                  background: "transparent",
                  border: "1px solid var(--strokeBase)",
                  color: "var(--fontSecondary)",
                  fontSize: "12px",
                  padding: "6px 12px",
                }}
              >
                Copy Address
              </CopyButton>
            )}
            <WalletConnect
              onAccountSelect={setSelectedAccount}
              selectedAccount={selectedAccount}
              showModal={showWalletModal}
              setShowModal={setShowWalletModal}
            />
          </WalletActions>
        </WalletSection>

        {/* Registration Panel */}
        <RegistrationPanel>
          <h3>Register DID</h3>

          {!selectedAccount ? (
            <ErrorMessage>
              Please connect a wallet using the button above to register a DID.
            </ErrorMessage>
          ) : (
            <>
              {existingDid ? (
                <WalletInfo>
                  <WalletAddress>
                    Address: {selectedAccount.address}
                  </WalletAddress>
                  <div
                    style={{
                      marginTop: "8px",
                      padding: "8px",
                      background: "#f0f0f0",
                      borderRadius: "4px",
                    }}
                  >
                    This account already has a DID registered:{" "}
                    {existingDid.identifier}
                  </div>
                </WalletInfo>
              ) : (
                <>
                  <WalletInfo>
                    <WalletAddress>
                      Address: {selectedAccount.address}
                    </WalletAddress>
                    <Balance>Balance: {balance}</Balance>
                  </WalletInfo>
                  {/* Registration Form */}
                  <FormSection>
                    <InputWrapper>
                      <Label>DID Identifier</Label>
                      <Flex gap={8}>
                        <Input
                          value={didIdentifier}
                          onChange={(e) => setDidIdentifier(e.target.value)}
                          placeholder="did:dropp:unique-identifier"
                          style={{ flex: 1 }}
                        />
                        <Button onClick={generateDidIdentifier}>
                          Generate
                        </Button>
                      </Flex>
                    </InputWrapper>

                    <InputWrapper>
                      <Label>Name (Optional)</Label>
                      <Input
                        value={didName}
                        onChange={(e) => setDidName(e.target.value)}
                        placeholder="My DID Name"
                      />
                    </InputWrapper>

                    <InputWrapper>
                      <Label>DID Document (JSON)</Label>
                      <TextArea
                        value={didDocument}
                        onChange={(e) => setDidDocument(e.target.value)}
                        placeholder='{"@context": "https://www.w3.org/ns/did/v1", "id": "did:dropp:..."}'
                        rows={5}
                      />
                    </InputWrapper>

                    {error && <ErrorMessage>{error}</ErrorMessage>}
                    {success && <SuccessMessage>{success}</SuccessMessage>}

                    <Button
                      onClick={handleRegisterDid}
                      disabled={loading || !didIdentifier || !didDocument}
                      style={{
                        width: "auto",
                        alignSelf: "flex-center",
                        padding: "10px 20px",
                        fontSize: "14px",
                      }}
                    >
                      {loading ? "Registering..." : "Register DID"}
                    </Button>
                  </FormSection>
                </>
              )}
            </>
          )}
        </RegistrationPanel>

        {/* DIDs List */}
        <DidsPanel>
          <h3>Registered DIDs ({totalDids})</h3>

          {loadingDids ? (
            <NoData>Loading DIDs...</NoData>
          ) : allDids.length === 0 ? (
            <NoData>No DIDs registered yet</NoData>
          ) : (
            <>
              <DidsTable>
                <thead>
                  <tr>
                    <th>Account</th>
                    <th>DID Identifier</th>
                    <th>Name</th>
                    <th>Role</th>
                    <th>Created At</th>
                    <th>Updated At</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedDids.map((did) => (
                    <tr key={did.account}>
                      <td>
                        <ColoredInterLink to={`/accounts/${did.account}`}>
                          {did.account.slice(0, 5)}...{did.account.slice(-5)}
                        </ColoredInterLink>
                      </td>
                      <td>{did.identifier}</td>
                      <td>{did.name || "-"}</td>
                      <td>
                        <Tag>{did.role}</Tag>
                      </td>
                      <td>{formatTimestamp(did.createdAt)}</td>
                      <td>{formatTimestamp(did.updatedAt)}</td>
                      <td>
                        <ViewButton onClick={() => viewDidDocument(did)}>
                          View Document
                        </ViewButton>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </DidsTable>

              {totalDids > itemsPerPage && (
                <Pagination
                  page={page}
                  pageSize={itemsPerPage}
                  total={totalDids}
                  setPage={(newPage) => setSearchParams({ page: newPage })}
                />
              )}
            </>
          )}
        </DidsPanel>
      </Wrapper>

      {/* Document Viewer Modal */}
      {showDocModal && selectedDidDoc && (
        <Modal onClick={() => setShowDocModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <FlexBetween style={{ marginBottom: "16px" }}>
              <h3>DID Document</h3>
              <SmallButton onClick={() => setShowDocModal(false)}>
                Close
              </SmallButton>
            </FlexBetween>

            <div style={{ marginBottom: "16px" }}>
              <strong>Account:</strong>{" "}
              <ColoredInterLink to={`/accounts/${selectedDidDoc.account}`}>
                {selectedDidDoc.account.slice(0, 8)}...
                {selectedDidDoc.account.slice(-8)}
              </ColoredInterLink>
            </div>

            <div style={{ marginBottom: "16px" }}>
              <strong>DID Identifier:</strong> {selectedDidDoc.identifier}
            </div>

            {selectedDidDoc.name && (
              <div style={{ marginBottom: "16px" }}>
                <strong>Name:</strong> {selectedDidDoc.name}
              </div>
            )}

            <div style={{ marginBottom: "16px" }}>
              <strong>Role:</strong> <Tag>{selectedDidDoc.role}</Tag>
            </div>

            <div>
              <strong>Document Content:</strong>
              <DocumentViewer>{selectedDidDoc.formattedDoc}</DocumentViewer>
            </div>

            <FlexBetween style={{ marginTop: "16px" }}>
              <CopyButton
                onClick={() => {
                  copyToClipboard(selectedDidDoc.formattedDoc);
                  dispatch(
                    addToast({ message: "Document copied!", type: "info" }),
                  );
                }}
              >
                Copy Document
              </CopyButton>
              <CopyButton
                onClick={() => {
                  copyToClipboard(selectedDidDoc.identifier);
                  dispatch(addToast({ message: "DID copied!", type: "info" }));
                }}
              >
                Copy DID
              </CopyButton>
            </FlexBetween>
          </ModalContent>
        </Modal>
      )}
    </Layout>
  );
}

export default DidPage;
